import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionLabel, TopWave, EASE } from "./shared";
import { scrollToId } from "./Header";
import { useLang } from "./i18n";

const PHASE_COLORS = ["#0E7C93", "#E5634F", "#A78BFA", "#22A55E"];

export function HowItWorks() {
  const { t } = useLang();
  return (
    <section id="how-it-works" className="grain t-bg-section relative py-24 md:py-36">
      <TopWave fill="rgb(var(--c-section))" />
      <div className="relative z-10 mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <div className="lg:sticky lg:top-32 lg:self-start">
          <Reveal>
            <SectionLabel dark>{t.hiw.label}</SectionLabel>
            <h2 className="t-text font-display text-4xl font-semibold leading-[1.08] tracking-[-0.02em] md:text-5xl">
              {t.hiw.titleA}
              <br />
              {t.hiw.titleB.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="italic text-maple">{t.hiw.titleB.split(" ").slice(-1)}</span>
            </h2>
            <p className="t-text-muted mt-6 max-w-md text-base leading-relaxed">{t.hiw.sub}</p>
            <button
              data-testid="hiw-start-btn"
              onClick={() => scrollToId("#quote")}
              className="group mt-9 flex items-center gap-2 rounded-full bg-maple px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#C23225]"
            >
              {t.hiw.cta}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>

        <div className="relative space-y-6">
          <div
            aria-hidden="true"
            className="absolute bottom-8 left-[27px] top-8 w-px"
            style={{ backgroundColor: "rgb(var(--c-on-deep) / 0.1)" }}
          />
          {t.hiw.phases.map((phase, i) => (
            <motion.div
              key={i}
              data-testid={`phase-card-${i}`}
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.05 * i, ease: EASE }}
              className="t-card relative ml-0 rounded-3xl p-7 transition-colors duration-300 md:p-9"
            >
              <div className="flex items-center justify-between">
                <span
                  className="rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em]"
                  style={{
                    background: `${PHASE_COLORS[i]}1A`,
                    color: PHASE_COLORS[i],
                    border: `1px solid ${PHASE_COLORS[i]}33`,
                  }}
                >
                  {phase.tag}
                </span>
                <span className="font-display text-5xl font-semibold" style={{ color: "rgb(var(--c-on-deep) / 0.1)" }}>
                  0{i + 1}
                </span>
              </div>
              <h3 className="t-text mt-5 font-display text-2xl font-semibold">{phase.title}</h3>
              <p className="t-text-muted mt-3 text-sm leading-relaxed">{phase.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
